import { ReactNode, useState } from "react";

import { MostRevenueAndRacetrackContainer } from "./MostRevenueAndRacetrackContainer";
import { TrophyHistoryDialog } from "./TrophyHistoryDialog";

import trophyCase from "@/assets/leaderboard/Trophy Case.svg";
import trophyPlate from "@/assets/leaderboard/trophy-plate.svg";
import ttfsTrophy from "@/assets/leaderboard/trophies/zeus-ttfs.webp";
import trophy1 from "@/assets/leaderboard/trophies/trophy-1.svg";
import trophy3 from "@/assets/leaderboard/trophies/trophy-3.svg";
import trophy4 from "@/assets/leaderboard/trophies/trophy-4.svg";
import trophy5 from "@/assets/leaderboard/trophies/trophy-5.svg";
import trophy6 from "@/assets/leaderboard/trophies/trophy-6.svg";
import trophy7 from "@/assets/leaderboard/trophies/trophy-7.svg";
import trophy8 from "@/assets/leaderboard/trophies/trophy-8.svg";
import {
  Revenue3,
  Revenue7,
  Revenue35,
} from "@/assets/leaderboard/trophies/revenue";
import { Column, ErrorText, Text } from "@/components";
import { Button } from "@/components/ui/button";

import { useGetStafferTrophies } from "@/hooks/queries/useGetStafferTrophies";
import { trophies as trophyFormatting } from "@/lib/trophies";

import { LeaderboardQueryParams, TrophyAwardType } from "@/types";

const trophyAwards = [
  {
    description: "generated the most revenue in a single month.",
    dialogTitle: "Revenue Leader",
    height: "24.8%",
    left: "7.5%",
    src: trophy1,
    title: "Revenue leader",
    type: TrophyAwardType.REVENUE,
  },
  {
    description:
      "had the lowest median time to first screen in a single month, excluding job pauses and time spent rejected.",
    dialogTitle: "Time to First Screen",
    height: "33%",
    left: "18%",
    src: ttfsTrophy,
    title: "Best TTFS",
    type: TrophyAwardType.BEST_TTFS,
  },
  {
    description: "made the most placements in a single month.",
    dialogTitle: "Placement Pro",
    height: "24.2%",
    left: "32.5%",
    src: trophy3,
    title: "Placement pro",
    type: TrophyAwardType.PLACEMENTS,
  },
  {
    description: "made the most internally sourced placements in a month.",
    dialogTitle: "Internal Placement Leader",
    height: "33.6%",
    left: "43%",
    src: trophy4,
    title: "Internal Placements",
    type: TrophyAwardType.INTERNAL_PLACEMENTS,
  },

  {
    description: "completed the most screens in a month.",
    dialogTitle: "Screening Leader",
    height: "27%",
    left: "57.5%",
    src: trophy5,
    title: "Screening leader",
    type: TrophyAwardType.SCREENS,
  },
  {
    description: "made the most candidate submissions in a month.",
    dialogTitle: "Submission Leader",
    height: "33.1%",
    left: "68%",
    src: trophy6,
    title: "Submission leader",
    type: TrophyAwardType.SUBMITTALS,
  },
  {
    description: "completed the most interviews in a month.",
    dialogTitle: "Interview Leader",
    height: "30.5%",
    left: "82.5%",
    src: trophy7,
    title: "Interview leader",
    type: TrophyAwardType.INTERVIEWS,
  },
  {
    description:
      "made placements across the most different companies in a month.",
    dialogTitle: "Most Versatile Staffer",
    height: "25%",
    left: "93%",
    src: trophy8,
    title: "Most versatile",
    type: TrophyAwardType.MOST_VERSATILE,
  },
];

// Per-winner revenue trophy art, keyed by user id.
const revenueTrophyByUserId = new Map<
  number,
  (props: { className?: string }) => ReactNode
>([
  [3, Revenue3],
  [7, Revenue7],
  [35, Revenue35],
]);

// Custom revenue art is drawn thinner than the stock trophies — scale it up.
const REVENUE_TROPHY_SCALE = 1.25;

const trophyCubbies = [
  trophyAwards.slice(0, 2),
  trophyAwards.slice(2, 4),
  trophyAwards.slice(4, 6),
  trophyAwards.slice(6, 8),
];

const BUSH_RUN_QUOTE = '"The legendary Bush run"';
const FIRST_1M = '"The First to 1M ARR"';

export const TrophyCase = ({
  queryParams,
}: {
  queryParams: LeaderboardQueryParams;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data, isError, isPending } = useGetStafferTrophies();

  /* React Hooks */
  const [selectedTrophyIndex, setSelectedTrophyIndex] = useState<number | null>(
    null,
  );

  /* Helpers */
  const getAward = (type: TrophyAwardType) =>
    data?.awards.find((award) => award.type === type);

  const getLeaders = (type: TrophyAwardType) => {
    const leaders = getAward(type)?.leaders;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return leaders || [];
  };

  // Revenue winners get custom trophy art when we have one for them.
  const getRevenueTrophy = (type: TrophyAwardType) => {
    if (type !== TrophyAwardType.REVENUE) return null;
    const winnerId = getAward(type)?.leaders[0]?.userId;
    if (winnerId === undefined) return null;
    return revenueTrophyByUserId.get(winnerId) ?? null;
  };

  /* Handlers */
  const handleDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen) setSelectedTrophyIndex(null);
  };

  /* Render */
  const renderTrophyArt = (trophy: (typeof trophyAwards)[number]) => {
    const RevenueTrophy = getRevenueTrophy(trophy.type);
    if (RevenueTrophy) {
      return (
        <Column
          className="h-full origin-bottom"
          style={{ transform: `scale(${String(REVENUE_TROPHY_SCALE)})` }}
        >
          <RevenueTrophy className="h-full w-auto max-w-none shrink-0" />
        </Column>
      );
    }
    return (
      <img
        src={trophy.src}
        alt={trophy.dialogTitle}
        className="h-full w-auto"
      />
    );
  };

  const render = () => {
    if (isPending) return null;
    if (isError)
      return <ErrorText errorMessage="Unable to load trophy case." />;
    return (
      <Column className="relative">
        <Column className="invisible">
          <MostRevenueAndRacetrackContainer
            queryParams={queryParams}
            isNaturalHeight
          />
        </Column>
        <Column className="absolute inset-0 overflow-hidden rounded-2xl shadow-sm">
          <img
            src={trophyCase}
            alt="Trophy case"
            className="block h-full w-full object-fill"
          />
          {trophyAwards.map((trophy, index) => (
            <Button
              key={trophy.src}
              variant="ghost"
              // Button's base styles force svgs to 16px — trophy art sizes itself.
              className="absolute -translate-x-1/2 p-0 hover:scale-105 hover:bg-transparent [&_svg]:size-auto [&_svg]:h-full [&_svg]:w-auto"
              style={{
                bottom: "calc(17% + 32px)",
                height: trophy.height,
                left: trophy.left,
              }}
              onClick={() => {
                setSelectedTrophyIndex(index);
              }}
            >
              {renderTrophyArt(trophy)}
            </Button>
          ))}
          {trophyCubbies.map((cubby, cubbyIndex) => (
            <Column
              key={cubbyIndex}
              className="pointer-events-none absolute inset-y-0 w-[20%] -translate-x-1/2"
              style={{ left: `${String(12.5 + cubbyIndex * 25)}%` }}
            >
              {cubby.map((trophy, plaqueIndex) => {
                const leader = getAward(trophy.type)?.leaders[0];
                const detail = leader
                  ? `${leader.firstName} · ${trophyFormatting.value(
                      trophy.type,
                      leader.value,
                    )}`
                  : "No winner yet";
                return (
                  <Column
                    key={trophy.title}
                    className="absolute left-1/2 h-12 w-[120%] -translate-x-1/2 items-center justify-center px-2 text-center"
                    style={{
                      backgroundImage: `url("${trophyPlate}")`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "100% 100%",
                      ...(plaqueIndex === 0 ? { top: "8%" } : { bottom: "8%" }),
                    }}
                  >
                    <Text className="whitespace-nowrap text-[10px] font-bold uppercase leading-tight tracking-wide text-amber-950">
                      {trophy.title}
                    </Text>
                    <Text className="whitespace-nowrap text-[10px] font-semibold leading-tight text-amber-950">
                      {detail}
                    </Text>
                    {trophy.type === TrophyAwardType.INTERVIEWS &&
                    leader?.userId === 35 ? (
                      <Text className="whitespace-nowrap text-[8px] font-medium italic leading-tight text-amber-900">
                        {BUSH_RUN_QUOTE}
                      </Text>
                    ) : null}
                    {trophy.type === TrophyAwardType.REVENUE &&
                    leader?.userId === 3 ? (
                      <Text className="whitespace-nowrap text-[8px] font-medium italic leading-tight text-amber-900">
                        {FIRST_1M}
                      </Text>
                    ) : null}
                  </Column>
                );
              })}
            </Column>
          ))}
        </Column>
        {selectedTrophyIndex === null ? null : (
          <TrophyHistoryDialog
            description={trophyAwards[selectedTrophyIndex].description}
            isOpen
            leaders={getLeaders(trophyAwards[selectedTrophyIndex].type)}
            minimumScreenCount={
              getAward(trophyAwards[selectedTrophyIndex].type)
                ?.minimumScreenCount
            }
            onOpenChange={handleDialogOpenChange}
            title={trophyAwards[selectedTrophyIndex].dialogTitle}
            trophyArt={renderTrophyArt(trophyAwards[selectedTrophyIndex])}
            type={trophyAwards[selectedTrophyIndex].type}
          />
        )}
      </Column>
    );
  };

  return render();
};
